import { useState, useEffect, useRef } from 'react';
import { C, FONT } from './adminShared';
import { useAuth } from '../../hooks/useAuth';
import { getDiscountWindow, setDiscountWindow } from '../../utils/api';

// ─── THE 48-HOUR DISCOUNT SWITCH ─────────────────────────────────────────────
// Ben presses this at the moment he sends the email that carries GO50GO. The
// code is valid for 48 hours from the press and goes dormant on its own.
//
// ⚠ THE CLOCK STARTS ON THE PRESS, so every hour it is armed early is an hour
// of the window wasted. That is the entire reason for the confirmation step
// below: this is not a settings toggle he can come back and adjust, it is a
// starting pistol.
//
// WHY IT LIVES ON THE REVENUE TAB: it changes what customers are charged and
// its headline number is how many people used it. That is a revenue fact, and
// this screen already computes `canWrite` from the owner role, so the gate the
// backend enforces is already expressed here.
//
// OWNER ONLY. The component renders NOTHING for a view-only admin — the arm
// endpoint is ownerMiddleware-gated and would 403, and a disabled button that
// errors when pressed is worse than a control that was never offered.

// The server is the authority on both the clock and the state. This component
// re-reads it rather than counting down from a locally-computed end time, so a
// device clock that is minutes out cannot make the page disagree with the
// checkout about whether the code is live.
const REFRESH_MS = 30 * 1000;

// "Ends Thursday 25 Sept, 4:12pm" — checkable against a real clock, which
// "48 hours remaining" is not. Rendered in the BROWSER's timezone, which on
// Ben's phone is Israel time; the underlying value is UTC and the window is
// measured in absolute time, so the display cannot move the deadline.
const fmtLocal = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'long', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit',
  });
};

// Whole units only — a seconds counter on a 48-hour window is noise, and it
// would force a re-render every second on a phone for no information gain.
const fmtRemaining = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return 'expired';
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const rMins = mins % 60;
  if (hours >= 1) return `${hours}h ${rMins}m left`;
  return `${mins}m left`;
};

const TONE = {
  armed:   { fg: C.green,  bg: C.greenBg, border: '#BFE3D2', word: 'ARMED'   },
  dormant: { fg: C.accent, bg: C.barTrack, border: C.border,  word: 'DORMANT' },
  expired: { fg: C.accent, bg: C.barTrack, border: C.border,  word: 'ENDED'   },
};

export const DiscountWindowCard = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [status, setStatus]   = useState(null);
  const [failed, setFailed]   = useState(false);
  const [stage, setStage]     = useState('idle');   // 'idle' | 'confirm' | 'working'
  const [error, setError]     = useState('');
  // Ticks the countdown between server reads so the "left" figure does not sit
  // frozen for 30s. It never decides anything — `status` is still the server's.
  const [, setTick]           = useState(0);
  const alive                 = useRef(true);

  const load = () => getDiscountWindow()
    .then(res => { if (alive.current) { setStatus(res.data); setFailed(false); } })
    .catch(() => { if (alive.current) setFailed(true); });

  useEffect(() => {
    alive.current = true;
    if (!isOwner) return undefined;
    load();
    const poll = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setTick(t => t + 1), 60 * 1000);
    return () => { alive.current = false; clearInterval(poll); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // C6: nothing at all for a non-owner, rather than a control that 403s.
  if (!isOwner) return null;

  const act = (action) => {
    setStage('working');
    setError('');
    setDiscountWindow(action)
      .then(res => { setStatus(res.data); setStage('idle'); })
      .catch(e => {
        setError(e?.response?.data?.error || e.message || 'That did not go through');
        setStage('idle');
      });
  };

  const state = status?.state || 'dormant';
  const tone  = TONE[state] || TONE.dormant;
  // Recomputed from the server's expiry against the browser clock, so the
  // figure keeps moving between the 30-second reads.
  const msLeft = status?.expiresAt ? (new Date(status.expiresAt).getTime() - Date.now()) : 0;

  const card = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: '18px 20px', marginTop: 24, fontFamily: FONT,
  };
  const btn = (bg, fg) => ({
    background: bg, color: fg, border: 'none', borderRadius: 8,
    padding: '11px 18px', fontSize: 13, fontWeight: 700, fontFamily: FONT,
    cursor: 'pointer',
    // Full width on a phone, natural width once there is room. Ben may well be
    // pressing this one-handed as he sends the email.
    flex: '1 1 160px', minWidth: 0,
  });

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>
          48-hour discount code
        </div>
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
          color: tone.fg, background: tone.bg, border: `1px solid ${tone.border}`,
          borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap',
        }}>
          {tone.word}
        </span>
      </div>

      <div style={{ fontSize: 11.5, color: C.accent, lineHeight: 1.6, marginBottom: 14 }}>
        <strong style={{ color: C.heading, fontStyle: 'normal' }}>{status?.code || 'GO50GO'}</strong>
        {' '}takes {status?.percentOff ?? 50}% off. It only works while armed, for{' '}
        {status?.windowHours ?? 48} hours from the moment you press.{' '}
        <strong style={{ color: C.heading }}>5050GO is separate and always on</strong> — nothing here touches it.
      </div>

      {failed && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 12 }}>
          Couldn't read the current state. The code's real status is whatever the
          checkout says — don't arm again until this loads.
        </div>
      )}

      {/* ── WHILE ARMED: the deadline, in words he can check against a clock ── */}
      {state === 'armed' && status?.expiresAt && (
        <div style={{
          background: C.greenBg, border: '1px solid #BFE3D2', borderRadius: 8,
          padding: '10px 12px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 12.5, color: C.heading, fontWeight: 700, lineHeight: 1.5 }}>
            Ends {fmtLocal(status.expiresAt)}
          </div>
          <div style={{ fontSize: 11, color: C.green, fontWeight: 700, marginTop: 2 }}>
            {fmtRemaining(msLeft)}
          </div>
        </div>
      )}

      {state === 'expired' && status?.expiresAt && (
        <div style={{ fontSize: 11.5, color: C.accent, marginBottom: 14, lineHeight: 1.5 }}>
          Last window ended {fmtLocal(status.expiresAt)}. Anyone typing the code now
          is told the offer has ended and sees the full price.
        </div>
      )}

      {/* ── USAGE ─────────────────────────────────────────────────────────────
          Two numbers, because one does not answer the question: "this window"
          is how the current send is performing, "all time" is whether the code
          has leaked beyond the people he meant to give it to. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          { label: state === 'armed' ? 'Used this window' : 'Used last window', val: status?.usesWindow ?? 0 },
          { label: 'Used all time', val: status?.usesTotal ?? 0 },
        ].map(t => (
          <div key={t.label} style={{
            flex: '1 1 120px', background: '#FDFAF8', border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '9px 10px', textAlign: 'center', minWidth: 0,
          }}>
            <div style={{ fontSize: 9, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.heading }}>{t.val}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>{error}</div>
      )}

      {/* ── THE CONTROLS ─────────────────────────────────────────────────────
          C2: arming is a two-press action. An accidental press wastes part of
          a window Ben cannot get back, and the confirmation says plainly that
          the clock starts NOW rather than asking a vague "are you sure?". */}
      {stage === 'confirm' ? (
        <div style={{
          background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 8,
          padding: '12px 14px',
        }}>
          <div style={{ fontSize: 12.5, color: '#92650A', lineHeight: 1.6, marginBottom: 12 }}>
            The {status?.windowHours ?? 48} hours starts the moment you press —
            not when you send the email. Only press this if the email is going out now.
            {status?.state === 'armed' && (
              <> <strong>This will also restart the window that is already running.</strong></>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => act('arm')} style={btn(C.green, '#fff')}>
              Yes — start the {status?.windowHours ?? 48} hours now
            </button>
            <button onClick={() => setStage('idle')} style={btn(C.barTrack, C.heading)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setStage('confirm')}
            disabled={stage === 'working'}
            style={{ ...btn(stage === 'working' ? C.barTrack : C.cta, stage === 'working' ? C.accent : '#fff'),
                     cursor: stage === 'working' ? 'not-allowed' : 'pointer' }}
          >
            {stage === 'working' ? 'Working…' : (state === 'armed' ? 'Restart the window' : 'Arm the code')}
          </button>
          {state === 'armed' && (
            // Turning it off early needs no confirmation: it is reversible in
            // one press, and the cost of an accidental disarm is that he arms
            // it again — not a window he cannot recover.
            <button onClick={() => act('disarm')} disabled={stage === 'working'} style={btn(C.barTrack, C.heading)}>
              Turn off now
            </button>
          )}
        </div>
      )}

      <div style={{ fontSize: 10, color: C.accent, marginTop: 12, lineHeight: 1.5 }}>
        Nothing runs on a schedule to end this — the code simply stops working once
        the window passes. Times shown are your device's local time.
      </div>
    </div>
  );
};
